import React, { useCallback, useMemo, useState } from 'react';

import type {
  Project,
  UploadedFile,
  Candidate,
  Criterion,
  Evaluation,
  Citation,
} from './types';
import { MOCK_PROJECTS } from './mocks';
import ProjectsDashboard from './screens/ProjectsDashboard';
import ProjectWorkspace from './screens/ProjectWorkspace';

/**
 * Point d'entrée du module Analyse d'appels d'offres.
 *
 * - Pas de router : l'écran courant est déterminé par `activeProjectId`.
 *   (null → dashboard, sinon → workspace unifié du projet)
 * - Toutes les mutations passent par un `updateProject(id, patch|fn)` centralisé
 *   pour garantir l'immutabilité et rafraîchir la date `updatedAt`.
 * - Les handlers "action IA" (ré-extraction critères, scoring, export RAO…)
 *   sont des stubs pour le prototype — on n'appelle pas de vrai backend.
 */
const AnalyseAO: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>(MOCK_PROJECTS);
  const [activeProjectId, setActiveProjectId] = useState<string | null>(null);

  const activeProject = useMemo(
    () => projects.find(p => p.id === activeProjectId) ?? null,
    [projects, activeProjectId]
  );

  // ── Helpers projet ─────────────────────────────────────────────────
  const updateProject = useCallback(
    (id: string, updater: (p: Project) => Project) => {
      setProjects(prev =>
        prev.map(p => (p.id === id ? { ...updater(p), updatedAt: new Date().toISOString() } : p))
      );
    },
    []
  );

  // ── Navigation ─────────────────────────────────────────────────────
  const handleOpenProject = useCallback((id: string) => {
    setActiveProjectId(id);
  }, []);

  const handleCreateProject = useCallback((name: string, reference?: string) => {
    const id = `p-${Date.now()}`;
    const now = new Date().toISOString();
    const newProject: Project = {
      id,
      name,
      reference,
      status: 'in_progress',
      createdAt: now,
      updatedAt: now,
      files: [],
      candidates: [],
      criteria: [],
      evaluations: [],
      citations: [],
      criteriaValidated: false,
    };
    setProjects(prev => [newProject, ...prev]);
    setActiveProjectId(id);
  }, []);

  const handleDeleteProject = useCallback(
    (id: string) => {
      setProjects(prev => prev.filter(p => p.id !== id));
      if (activeProjectId === id) {
        setActiveProjectId(null);
      }
    },
    [activeProjectId]
  );

  const backToDashboard = useCallback(() => {
    setActiveProjectId(null);
  }, []);

  // ── Handlers Fichiers ──────────────────────────────────────────────
  const handleAddRegulationFiles = useCallback(
    (files: File[]) => {
      if (!activeProjectId) return;
      const now = new Date().toISOString();
      const newFiles: UploadedFile[] = files.map((f, i) => ({
        id: `f-${Date.now()}-${i}`,
        name: f.name,
        sizeBytes: f.size,
        pages: Math.ceil(f.size / 50_000),
        uploadedAt: now,
        source: 'regulation',
      }));
      updateProject(activeProjectId, p => ({ ...p, files: [...p.files, ...newFiles] }));
    },
    [activeProjectId, updateProject]
  );

  const handleAddCandidate = useCallback(
    (name: string) => {
      if (!activeProjectId) return;
      const candidate: Candidate = {
        id: `c-${Date.now()}`,
        name,
        fileIds: [],
        totalPages: 0,
        status: 'todo',
      };
      updateProject(activeProjectId, p => ({
        ...p,
        candidates: [...p.candidates, candidate],
      }));
    },
    [activeProjectId, updateProject]
  );

  const handleAddCandidateFiles = useCallback(
    (candidateId: string, files: File[]) => {
      if (!activeProjectId) return;
      const now = new Date().toISOString();
      const newFiles: UploadedFile[] = files.map((f, i) => ({
        id: `f-${Date.now()}-${i}`,
        name: f.name,
        sizeBytes: f.size,
        pages: Math.ceil(f.size / 50_000),
        uploadedAt: now,
        source: 'candidate',
        candidateId,
      }));
      updateProject(activeProjectId, p => ({
        ...p,
        files: [...p.files, ...newFiles],
        candidates: p.candidates.map(c =>
          c.id === candidateId
            ? {
                ...c,
                fileIds: [...c.fileIds, ...newFiles.map(nf => nf.id)],
                totalPages: c.totalPages + newFiles.reduce((s, f) => s + (f.pages ?? 0), 0),
                status: c.status === 'todo' ? 'in_progress' : c.status,
              }
            : c
        ),
      }));
    },
    [activeProjectId, updateProject]
  );

  const handleRemoveFile = useCallback(
    (fileId: string) => {
      if (!activeProjectId) return;
      updateProject(activeProjectId, p => {
        const file = p.files.find(f => f.id === fileId);
        if (!file) return p;
        return {
          ...p,
          files: p.files.filter(f => f.id !== fileId),
          candidates: p.candidates.map(c =>
            c.fileIds.includes(fileId)
              ? {
                  ...c,
                  fileIds: c.fileIds.filter(x => x !== fileId),
                  totalPages: Math.max(0, c.totalPages - (file.pages ?? 0)),
                }
              : c
          ),
        };
      });
    },
    [activeProjectId, updateProject]
  );

  const handleRemoveCandidate = useCallback(
    (candidateId: string) => {
      if (!activeProjectId) return;
      updateProject(activeProjectId, p => ({
        ...p,
        candidates: p.candidates.filter(c => c.id !== candidateId),
        files: p.files.filter(f => f.candidateId !== candidateId),
        evaluations: p.evaluations.filter(e => e.candidateId !== candidateId),
        citations: p.citations.filter(c => {
          const target = p.evaluations.find(e => e.id === c.evaluationId);
          return !target || target.candidateId !== candidateId;
        }),
      }));
    },
    [activeProjectId, updateProject]
  );

  // ── Handlers Critères ──────────────────────────────────────────────
  const handleCreateCriterion = useCallback(
    (c: Omit<Criterion, 'id' | 'order'>) => {
      if (!activeProjectId) return;
      updateProject(activeProjectId, p => {
        const siblingOrders = p.criteria
          .filter(x => x.parentId === c.parentId)
          .map(x => x.order);
        const order = siblingOrders.length ? Math.max(...siblingOrders) + 1 : 0;
        const criterion: Criterion = { ...c, id: `k-${Date.now()}`, order };
        return { ...p, criteria: [...p.criteria, criterion] };
      });
    },
    [activeProjectId, updateProject]
  );

  const handleUpdateCriterion = useCallback(
    (id: string, patch: Partial<Criterion>) => {
      if (!activeProjectId) return;
      updateProject(activeProjectId, p => ({
        ...p,
        criteria: p.criteria.map(c => (c.id === id ? { ...c, ...patch } : c)),
      }));
    },
    [activeProjectId, updateProject]
  );

  const handleDeleteCriterion = useCallback(
    (id: string) => {
      if (!activeProjectId) return;
      updateProject(activeProjectId, p => {
        // Supprime le critère + descendants (BFS)
        const toRemove = new Set<string>([id]);
        const childrenByParent = new Map<string, string[]>();
        p.criteria.forEach(c => {
          if (c.parentId) {
            const bucket = childrenByParent.get(c.parentId) ?? [];
            bucket.push(c.id);
            childrenByParent.set(c.parentId, bucket);
          }
        });
        const queue: string[] = [id];
        while (queue.length > 0) {
          const current = queue.shift()!;
          const kids = childrenByParent.get(current) ?? [];
          kids.forEach(k => {
            if (!toRemove.has(k)) {
              toRemove.add(k);
              queue.push(k);
            }
          });
        }
        return {
          ...p,
          criteria: p.criteria.filter(c => !toRemove.has(c.id)),
          evaluations: p.evaluations.filter(e => !toRemove.has(e.criterionId)),
        };
      });
    },
    [activeProjectId, updateProject]
  );

  const handleToggleCriterionActive = useCallback(
    (id: string, active: boolean) => {
      if (!activeProjectId) return;
      updateProject(activeProjectId, p => ({
        ...p,
        criteria: p.criteria.map(c => (c.id === id ? { ...c, active } : c)),
      }));
    },
    [activeProjectId, updateProject]
  );

  const handleReextractCriteria = useCallback(() => {
    // Stub prototype : dans la vraie app on appellerait l'extraction IA.
    // Ici on ne fait rien (les mocks sont déjà fournis).
  }, []);

  const handleValidateCriteria = useCallback(() => {
    if (!activeProjectId) return;
    updateProject(activeProjectId, p => ({ ...p, criteriaValidated: true }));
  }, [activeProjectId, updateProject]);

  // ── Handlers Évaluations ───────────────────────────────────────────
  const handleUpdateEvaluation = useCallback(
    (candidateId: string, criterionId: string, patch: Partial<Evaluation>) => {
      if (!activeProjectId) return;
      updateProject(activeProjectId, p => {
        const existingIdx = p.evaluations.findIndex(
          e => e.candidateId === candidateId && e.criterionId === criterionId
        );
        if (existingIdx !== -1) {
          const existing = p.evaluations[existingIdx];
          const updated: Evaluation = { ...existing, ...patch };
          const next = [...p.evaluations];
          next[existingIdx] = updated;
          return { ...p, evaluations: next };
        }
        const newEval: Evaluation = {
          id: `e-${candidateId}-${criterionId}`,
          projectId: p.id,
          candidateId,
          criterionId,
          citationIds: [],
          ...patch,
        };
        return { ...p, evaluations: [...p.evaluations, newEval] };
      });
    },
    [activeProjectId, updateProject]
  );

  const handleAddCitation = useCallback(
    (evaluationId: string, text: string, sourceFileId: string, sourcePage: number) => {
      if (!activeProjectId) return;
      updateProject(activeProjectId, p => {
        const target = p.evaluations.find(e => e.id === evaluationId);
        if (!target) return p;
        const citation: Citation = {
          id: `cit-${Date.now()}`,
          evaluationId,
          text,
          sourceFileId,
          sourcePage,
          verified: false,
          origin: 'manual',
        };
        return {
          ...p,
          citations: [...p.citations, citation],
          evaluations: p.evaluations.map(e =>
            e.id === evaluationId ? { ...e, citationIds: [...e.citationIds, citation.id] } : e
          ),
        };
      });
    },
    [activeProjectId, updateProject]
  );

  const handleVerifyCitation = useCallback(
    (citationId: string, verified: boolean) => {
      if (!activeProjectId) return;
      updateProject(activeProjectId, p => ({
        ...p,
        citations: p.citations.map(c => (c.id === citationId ? { ...c, verified } : c)),
      }));
    },
    [activeProjectId, updateProject]
  );

  const handleBulkVerifyCitations = useCallback(
    (citationIds: string[], verified: boolean) => {
      if (!activeProjectId || citationIds.length === 0) return;
      const idSet = new Set(citationIds);
      updateProject(activeProjectId, p => ({
        ...p,
        citations: p.citations.map(c => (idSet.has(c.id) ? { ...c, verified } : c)),
      }));
    },
    [activeProjectId, updateProject]
  );

  const handleRemoveCitation = useCallback(
    (citationId: string) => {
      if (!activeProjectId) return;
      updateProject(activeProjectId, p => ({
        ...p,
        citations: p.citations.filter(c => c.id !== citationId),
        evaluations: p.evaluations.map(e => ({
          ...e,
          citationIds: e.citationIds.filter(id => id !== citationId),
        })),
      }));
    },
    [activeProjectId, updateProject]
  );

  // Stubs pour les boutons IA / export
  const handleRunAiScoring = useCallback(() => {}, []);
  const handleExportRao = useCallback(() => {}, []);

  // ── Rendu ──────────────────────────────────────────────────────────
  if (!activeProject) {
    return (
      <ProjectsDashboard
        projects={projects}
        onOpenProject={handleOpenProject}
        onCreateProject={handleCreateProject}
        onDeleteProject={handleDeleteProject}
      />
    );
  }

  return (
    <ProjectWorkspace
      project={activeProject}
      onBack={backToDashboard}
      onDelete={() => handleDeleteProject(activeProject.id)}
      onAddRegulationFiles={handleAddRegulationFiles}
      onAddCandidate={handleAddCandidate}
      onAddCandidateFiles={handleAddCandidateFiles}
      onRemoveFile={handleRemoveFile}
      onRemoveCandidate={handleRemoveCandidate}
      onCreateCriterion={handleCreateCriterion}
      onUpdateCriterion={handleUpdateCriterion}
      onDeleteCriterion={handleDeleteCriterion}
      onToggleCriterionActive={handleToggleCriterionActive}
      onReextractCriteria={handleReextractCriteria}
      onValidateCriteria={handleValidateCriteria}
      onUpdateEvaluation={handleUpdateEvaluation}
      onAddCitation={handleAddCitation}
      onVerifyCitation={handleVerifyCitation}
      onBulkVerifyCitations={handleBulkVerifyCitations}
      onRemoveCitation={handleRemoveCitation}
      onRunAiScoring={handleRunAiScoring}
      onExportRao={handleExportRao}
    />
  );
};

export default AnalyseAO;
